/**
 * WwsAddBar — Warenwirtschafts-Universalsuche + EAN-Scan-Leiste (· Jun-2026).
 *
 * EIN direktes Suchfeld über ALLE WWS-Daten (Lohn & Teile) — SERVERSEITIG
 * (gleiche Volltext-/Index-Suche wie in der Warenwirtschaft, performant auch
 * bei 50k+ Artikeln). Tippen → Treffer erscheinen sofort darunter, Klick ruft
 * `onAddArticle(article)` auf. Das EAN-Feld unterstützt Hand-/Tastatur-Scanner
 * (Eingabe + Enter) und ruft `onAddEan(code)` auf.
 *
 * Optional bei „kein Treffer" (User-Wunsch Jun-2026):
 *   - `onCreateArticle(query)`  → vollwertigen WWS-Artikel anlegen
 *   - `onAddFreeText(query)`    → schnelle freie Position übernehmen
 * Lager-Warnung im Dropdown: rot bei 0 Bestand, amber bei knappem Bestand.
 *
 * Wiederverwendbar in Werkstattaufträgen UND im Direktverkauf/Rechnungen.
 */
import { useState, useEffect } from "react";
import { Search, Barcode, Camera, Loader2, Plus } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import EanCameraScanner from "./EanCameraScanner";

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmtNum = (v) => Number(v || 0).toLocaleString("de-DE");
const priceNet = (a) => Number(a.sell_price_net || (a.sell_price_gross / (1 + (a.vat_rate || 19) / 100)) || 0);

export default function WwsAddBar({ onAddArticle, onAddEan, onCreateArticle, onAddFreeText, testidPrefix = "wws", autoScan = false, header = null, headerActions = null, hideActions = false, scanOpenExternal = undefined, onScanOpenChange = null }) {
    const [query, setQuery] = useState("");
    const [focused, setFocused] = useState(false);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ean, setEan] = useState("");
    const [scanOpenInt, setScanOpenInt] = useState(autoScan);
    // Kontrollierter Modus (Jul-2026): Eltern-Komponente kann das Scan-Modal öffnen.
    const scanOpen = scanOpenExternal !== undefined ? scanOpenExternal : scanOpenInt;
    const setScanOpen = (v) => { if (onScanOpenChange) onScanOpenChange(v); else setScanOpenInt(v); };

    // Serverseitige Volltext-Suche (gleiche Index-Suche wie im WWS-Modul), debounced.
    useEffect(() => {
        const q = query.trim();
        if (q.length < 1) { setResults([]); setLoading(false); return; }
        let active = true;
        setLoading(true);
        const h = setTimeout(async () => {
            try {
                const r = await api.get("/articles", { params: { search: q, active_only: true, limit: 30 } });
                if (active) setResults(Array.isArray(r.data) ? r.data : (r.data?.items || []));
            } catch { if (active) setResults([]); }
            finally { if (active) setLoading(false); }
        }, 250);
        return () => { active = false; clearTimeout(h); };
    }, [query]);

    const close = () => { setQuery(""); setResults([]); setFocused(false); };
    const pick = (a) => { onAddArticle(a); close(); };
    const submitEan = () => { if (ean.trim()) { onAddEan(ean.trim()); setEan(""); } };
    const showDropdown = focused && query.trim().length > 0;
    const term = query.trim();

    const StockLabel = ({ a }) => {
        if (a.category === "labour") return null;
        const s = Number(a.stock_qty ?? 0);
        const cls = s <= 0 ? "text-red-600 font-bold" : s <= 3 ? "text-amber-600 font-semibold" : "text-gray-500";
        const txt = s <= 0 ? "Kein Bestand" : s <= 3 ? `nur ${fmtNum(s)} auf Lager` : `Bestand ${fmtNum(s)}`;
        return <span className={cls}> · {txt}</span>;
    };

    return (
        <div className="mb-2" data-testid={`${testidPrefix}-addbar`}>
            {/* Jul-2026 (User-Wunsch): 2 Zeilen — oben Überschrift + Kamera-Button,
                darunter die Eingabefelder auf gleicher Höhe. */}
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="min-w-0">{header}</div>
                {!hideActions && (
                    <div className="flex items-center gap-2 shrink-0">
                        {/* Jul-2026 (User-Wunsch): Kamera-Button pulsiert orange, hervorgehoben. */}
                        <Button type="button" className="h-8 rounded-sm shrink-0 inline-flex items-center gap-1.5 text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white border-orange-500 animate-pulse" title="Mit Kamera scannen"
                            onClick={() => setScanOpen(true)} data-testid={`${testidPrefix}-camera-btn`}>
                            <Camera className="!w-4 !h-4" /> Mit Kamera scannen
                        </Button>
                        {headerActions}
                    </div>
                )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                {loading && <Loader2 className="w-4 h-4 text-gray-400 animate-spin absolute right-2.5 top-1/2 -translate-y-1/2" />}
                <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 180)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (results[0]) pick(results[0]); } }}
                    placeholder="Warenwirtschaft durchsuchen (Teile & Leistungen)…"
                    className="rounded-sm h-9 pl-8 pr-8 text-sm w-full"
                    data-testid={`${testidPrefix}-search-input`}
                />
                {showDropdown && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-lg max-h-72 overflow-y-auto" data-testid={`${testidPrefix}-results`}>
                        {loading && results.length === 0 ? (
                            <div className="px-3 py-2.5 text-xs text-gray-400">Suche…</div>
                        ) : results.length === 0 ? (
                            <div className="px-3 py-2.5" data-testid={`${testidPrefix}-empty`}>
                                <div className="text-xs text-gray-400 mb-2">Kein Treffer für „{term}".</div>
                                {(onCreateArticle || onAddFreeText) && (
                                    <div className="flex flex-col gap-1.5">
                                        {onCreateArticle && (
                                            <button type="button"
                                                onMouseDown={(e) => { e.preventDefault(); const t = term; close(); onCreateArticle(t); }}
                                                className="w-full text-left px-2.5 py-2 rounded-sm border border-[#FFCC00] bg-[#FFFBEB] hover:bg-[#FFF3C4] text-xs font-bold text-[#111827] flex items-center gap-1.5"
                                                data-testid={`${testidPrefix}-create-article`}>
                                                <Plus className="w-3.5 h-3.5 shrink-0" /> Als WWS-Artikel „{term}" anlegen
                                            </button>
                                        )}
                                        {onAddFreeText && (
                                            <button type="button"
                                                onMouseDown={(e) => { e.preventDefault(); const t = term; close(); onAddFreeText(t); }}
                                                className="w-full text-left px-2.5 py-2 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-medium text-gray-700 flex items-center gap-1.5"
                                                data-testid={`${testidPrefix}-add-free`}>
                                                <Plus className="w-3.5 h-3.5 shrink-0" /> Als freie Position übernehmen
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : results.map((a) => (
                            <button
                                key={a.id}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); pick(a); }}
                                className="w-full text-left px-3 py-2 hover:bg-[#FFFBEB] flex flex-col border-b border-gray-100 last:border-0"
                                data-testid={`${testidPrefix}-opt-${a.id}`}
                            >
                                <span className="font-medium text-sm flex items-center gap-1.5">
                                    <Badge className={`shrink-0 text-[9px] font-bold uppercase rounded-sm ${a.category === "labour" ? "bg-blue-200 text-blue-900" : "bg-gray-200 text-gray-800"}`}>{a.category === "labour" ? "Lohn" : "Teil"}</Badge>
                                    {a.name}
                                </span>
                                <span className="text-[10px] text-gray-500">
                                    {a.sku} · {fmtEur(priceNet(a))} netto<StockLabel a={a} />{a.is_differenzbesteuerung ? " · §25a" : ""}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {/* Jul-2026 (User-Wunsch): nur noch das EAN-Feld — Kamera-Button sitzt in der Kopfzeile. */}
            <div className="relative sm:w-64 shrink-0">
                <Barcode className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                    value={ean}
                    onChange={(e) => setEan(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitEan(); } }}
                    placeholder="EAN scannen/eingeben + Enter"
                    className="rounded-sm h-9 pl-8 text-sm w-full"
                    inputMode="numeric"
                    data-testid={`${testidPrefix}-ean-input`}
                />
            </div>
            </div>
            {scanOpen && (
                <EanCameraScanner
                    onDetected={(code) => { setScanOpen(false); onAddEan(String(code || "").trim()); }}
                    onClose={() => setScanOpen(false)}
                />
            )}
        </div>
    );
}
